import React, { useEffect, useState } from 'react';
import { Link } from '@/lib/router-compat';
import { useCategoryCounts } from '@/hooks/useCategoryCounts';
import { useCountry } from '@/hooks/useCountry';
import { useLang, type Lang } from '@/i18n/useLang';
import type { VehicleType } from '@/types/car';
import imgCar from '@/assets/category-car.png';
import imgVan from '@/assets/category-van.png';
import imgMoto from '@/assets/category-motorcycle.png';
import imgTruck from '@/assets/category-truck.png';
import imgBoat from '@/assets/category-boat.png';
import imgConstruction from '@/assets/category-construction.png';
import imgTractor from '@/assets/category-tractor.png';

type Tile = {
  key: VehicleType;
  to: string;
  img: string;
  label: Record<Lang, string>;
  sub: Record<Lang, string>;
};

const TILES: Tile[] = [
  { key: 'car',          to: '/vehicles?type=car',          img: imgCar,
    label: { ro: 'Autoturisme',   en: 'Cars',         de: 'Pkw' },
    sub:   { ro: 'Berline, SUV, coupé',     en: 'Saloons, SUVs, coupés',    de: 'Limousinen, SUVs, Coupés' } },
  { key: 'van',          to: '/vehicles?type=van',          img: imgVan,
    label: { ro: 'Autoutilitare', en: 'Vans',         de: 'Transporter' },
    sub:   { ro: 'Transport marfă',         en: 'Cargo transport',          de: 'Warentransport' } },
  { key: 'motorcycle',   to: '/vehicles?type=motorcycle',   img: imgMoto,
    label: { ro: 'Motociclete',   en: 'Motorcycles',  de: 'Motorräder' },
    sub:   { ro: 'Sport, touring, scuter',  en: 'Sport, touring, scooter',  de: 'Sport, Touring, Roller' } },
  { key: 'truck',        to: '/vehicles?type=truck',        img: imgTruck,
    label: { ro: 'Camioane',      en: 'Trucks',       de: 'Lkw' },
    sub:   { ro: 'Cap tractor & remorci',   en: 'Tractor units & trailers', de: 'Zugmaschinen & Anhänger' } },
  { key: 'boat',         to: '/vehicles?type=boat',         img: imgBoat,
    label: { ro: 'Ambarcațiuni',  en: 'Boats',        de: 'Boote' },
    sub:   { ro: 'Bărci, iahturi, jet ski', en: 'Boats, yachts, jet skis',  de: 'Boote, Yachten, Jet-Ski' } },
  { key: 'construction', to: '/vehicles?type=construction', img: imgConstruction,
    label: { ro: 'Utilaje',       en: 'Machinery',    de: 'Maschinen' },
    sub:   { ro: 'Excavatoare, încărcătoare', en: 'Excavators, loaders',    de: 'Bagger, Lader' } },
  { key: 'agricultural', to: '/vehicles?type=agricultural', img: imgTractor,
    label: { ro: 'Tractoare',     en: 'Tractors',     de: 'Traktoren' },
    sub:   { ro: 'Agricole & utilaje',      en: 'Agricultural & equipment', de: 'Landwirtschaft & Geräte' } },
];

const HEADING = { ro: 'Caută după tip', en: 'Search by type', de: 'Nach Typ suchen' } as const;
const VIEW_N  = { ro: (n: string) => `Vezi ${n} anunțuri`, en: (n: string) => `View ${n} listings`, de: (n: string) => `${n} Inserate ansehen` } as const;
const ALT_PREFIX = { ro: 'Categoria', en: 'Category', de: 'Kategorie' } as const;

const CategoryTilesV2: React.FC = () => {
  const { country } = useCountry();
  const lang = useLang();
  const { data } = useCategoryCounts(country);
  const counts = data?.counts;

  // Defer heavy image rendering until AFTER LCP so category PNGs don't
  // compete with the hero image for bandwidth. Placeholders keep layout stable.
  const [showImages, setShowImages] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setShowImages(true); };
    const win: any = typeof window !== 'undefined' ? window : null;
    if (win?.requestIdleCallback) {
      const id = win.requestIdleCallback(reveal, { timeout: 2500 });
      return () => { cancelled = true; win.cancelIdleCallback?.(id); };
    }
    const t = setTimeout(reveal, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const getCount = (key: VehicleType): number => (counts ? counts[key] || 0 : 0);
  const localeFor = lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-GB' : 'ro-RO';

  return (
    <section className="container mx-auto px-4 py-10">
      <h2 className="font-serif text-2xl md:text-3xl mb-6 text-center">{HEADING[lang]}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {TILES.filter(({ key }) => !counts || key === 'car' || getCount(key) > 0 || (key === 'motorcycle' && getCount('atv' as VehicleType) > 0)).map(({ key, label, sub, to, img }) => {
          const count = getCount(key);
          return (
            <Link
              key={key}
              to={to}
              aria-label={label[lang]}
              className="group rounded border border-border bg-card hover:border-accent/40 transition-colors duration-200 p-5 flex flex-col items-center text-center"
            >
              <div className="w-full aspect-[4/3] flex items-center justify-center mb-3 overflow-hidden bg-muted/20 rounded-md">
                {showImages ? (
                  <img
                    src={img}
                    alt={`${ALT_PREFIX[lang]} ${label[lang]} : ${sub[lang]}`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    width={512}
                    height={512}
                    className="max-h-full max-w-full object-contain transition-transform duration-200"
                  />
                ) : null}
              </div>
              <div className="font-serif text-base md:text-lg text-primary leading-tight break-words w-full">{label[lang]}</div>
              <div className="text-xs md:text-sm text-muted-foreground mt-1 break-words w-full">{sub[lang]}</div>
              {count > 0 && <div className="text-xs text-muted-foreground mt-3">{VIEW_N[lang](count.toLocaleString(localeFor))}</div>}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default CategoryTilesV2;
